import React, { useState } from "react";
import { Link } from "react-router-dom";
import { KeyRound, AlertCircle, Loader2, CheckCircle2, MessageCircle } from "lucide-react";
import axios from "axios";
import { Tx } from "../context/AutoTranslateContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RecoverAccess = () => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);  // { reset_link, phone_used }

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError(""); setResult(null);
    try {
      const r = await axios.post(`${API}/auth/recover-access`, { email: email.trim() });
      setResult(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Recovery failed");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="recover-page" className="max-w-md mx-auto px-4 sm:px-8 py-16">
      <div className="clay-surface rounded-3xl p-8">
        <div className="text-center mb-6">
          <KeyRound className="w-10 h-10 text-gold mx-auto mb-3" />
          <h1 className="font-heading text-3xl text-forest font-bold mb-2">
            <Tx>Recover your account</Tx>
          </h1>
          <p className="text-sm text-mute leading-relaxed">
            <Tx>Enter the email you used to pay. We'll create your sign-in link right here on this page — no email needed.</Tx>
          </p>
        </div>

        {!result && (
          <form onSubmit={submit} className="space-y-4">
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-mute"><Tx>Email you paid with</Tx></span>
              <input
                data-testid="recover-email"
                type="email" required autoFocus
                value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full mt-1 px-4 py-2.5 rounded-full bg-cream border border-forest/10 focus:border-forest outline-none text-sm"
              />
            </label>
            {error && (
              <div className="flex items-start gap-2 text-sm text-clay bg-clay/10 rounded-2xl p-3">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" /> <span>{error}</span>
              </div>
            )}
            <button
              data-testid="recover-submit"
              type="submit" disabled={busy}
              className="w-full py-3 rounded-full bg-forest text-sand hover:bg-sage transition font-medium disabled:opacity-60"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : <Tx>Get my sign-in link</Tx>}
            </button>
          </form>
        )}

        {result && (
          <div data-testid="recover-result" className="space-y-4">
            <div className="flex items-start gap-2 text-sm text-forest bg-forest/5 rounded-2xl p-3">
              <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0" />
              <span><Tx>Found your account! Click below to set your password now.</Tx></span>
            </div>
            {result.phone_used && (
              <div className="flex items-start gap-2 text-xs text-gold bg-gold/10 rounded-2xl p-3">
                <MessageCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span><Tx>We also sent this link to your WhatsApp for safekeeping.</Tx></span>
              </div>
            )}
            {result.reset_link && (
              <a
                href={result.reset_link}
                data-testid="recover-open-link"
                className="w-full block text-center py-3 rounded-full bg-forest text-sand hover:bg-sage font-medium"
              >
                <Tx>Set my password →</Tx>
              </a>
            )}
            <p className="text-[11px] text-mute italic text-center">
              <Tx>This link works for 14 days. If you close this page, you can come back here anytime and get a fresh one.</Tx>
            </p>
          </div>
        )}

        <div className="mt-6 text-xs text-mute text-center">
          <Link to="/login" className="hover:text-forest"><Tx>← Back to sign in</Tx></Link>
        </div>
      </div>
    </div>
  );
};

export default RecoverAccess;
